import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { tokenService } from '../services/tokenService';

const API_SCOPE = [`api://${process.env.REACT_APP_AZURE_CLIENT_ID}/access_as_user`];

type UserGroupsContextValue = { groups: string[]; loading: boolean };

const defaultValue: UserGroupsContextValue = { groups: [], loading: true };
const UserGroupsContext = createContext<UserGroupsContextValue>(defaultValue);

export function UserGroupsProvider({ children }: { children: React.ReactNode }) {
  const { accounts } = useMsal();
  const [groups, setGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchedForRef = useRef<string | null>(null);

  useEffect(() => {
    const accountKey = accounts[0]?.homeAccountId ?? null;
    if (!accountKey) {
      setGroups([]);
      setLoading(false);
      fetchedForRef.current = null;
      return;
    }
    if (fetchedForRef.current === accountKey) {
      return;
    }
    fetchedForRef.current = accountKey;
    setLoading(true);

    const fetchGroups = async () => {
      try {
        const accessToken = await tokenService.getAccessToken(API_SCOPE);
        const res = await fetch('/api/user-groups', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = await res.json();
        if (data.groupNames && Array.isArray(data.groupNames)) {
          setGroups(data.groupNames);
        } else {
          setGroups([]);
        }
      } catch {
        setGroups([]);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, [accounts[0]?.homeAccountId]);

  return (
    <UserGroupsContext.Provider value={{ groups, loading }}>
      {children}
    </UserGroupsContext.Provider>
  );
}

export function useUserGroups(): UserGroupsContextValue {
  return useContext(UserGroupsContext);
}
